import { FormEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PageMeta } from '../components/PageMeta';
import { CONTACT } from '../config';

type ListSection = {
  title: string;
  items: string[];
};

export function DeleteAccountPage() {
  const { t } = useTranslation();
  const [status, setStatus] = useState<'idle' | 'ok' | 'err'>('idle');
  const steps = t('deleteAccountPage.steps', {
    returnObjects: true,
    privacyEmail: CONTACT.privacyEmail,
  }) as string[];
  const deleted = t('deleteAccountPage.deleted', { returnObjects: true }) as ListSection;
  const retained = t('deleteAccountPage.retained', { returnObjects: true }) as ListSection;

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setStatus('idle');
    const form = e.currentTarget;
    const data = new FormData(form);
    const body = new URLSearchParams();
    data.forEach((value, key) => {
      body.append(key, value.toString());
    });

    try {
      const res = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setStatus('ok');
        form.reset();
      } else {
        setStatus('err');
      }
    } catch {
      setStatus('err');
    }
  };

  return (
    <>
      <PageMeta
        titleKey="deleteAccountPage.metaTitle"
        descriptionKey="deleteAccountPage.metaDescription"
      />

      <div className="legal-page delete-account-page">
        <article className="legal-document delete-account-document">
          <h1 className="delete-account-title">{t('deleteAccountPage.title')}</h1>
          <p className="legal-paragraph">{t('deleteAccountPage.intro')}</p>

          <h2 className="legal-heading">{t('deleteAccountPage.stepsHeading')}</h2>
          <ol className="delete-account-steps">
            {steps.map((step) => (
              <li key={step.slice(0, 48)}>{step}</li>
            ))}
          </ol>

          <h2 className="legal-heading">{deleted.title}</h2>
          <ul className="legal-list">
            {deleted.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <h2 className="legal-heading">{retained.title}</h2>
          <ul className="legal-list">
            {retained.items.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>

          <p className="legal-paragraph">{t('deleteAccountPage.retentionNote')}</p>
          <p className="legal-paragraph">{t('deleteAccountPage.processingTime')}</p>

          <h2 className="legal-heading">{t('deleteAccountPage.requestHeading')}</h2>
          <p className="legal-paragraph">{t('deleteAccountPage.requestIntro')}</p>

          <form
            name="account-deletion-request"
            method="POST"
            data-netlify="true"
            data-netlify-honeypot="bot-field"
            onSubmit={handleSubmit}
            className="delete-account-form"
          >
            <input type="hidden" name="form-name" value="account-deletion-request" />
            <p className="honeypot">
              <label>
                Don’t fill this out: <input name="bot-field" />
              </label>
            </p>

            <div className="form-group">
              <label htmlFor="ad-email">{t('deleteAccountPage.formEmail')}</label>
              <input
                id="ad-email"
                name="accountEmail"
                type="email"
                required
                autoComplete="email"
              />
            </div>
            <div className="form-group">
              <label htmlFor="ad-msg">{t('deleteAccountPage.formMessage')}</label>
              <textarea id="ad-msg" name="message" />
            </div>

            <button type="submit" className="btn-primary">
              {t('deleteAccountPage.formSubmit')}
            </button>

            {status === 'ok' && (
              <p className="form-status form-status--ok" role="status">
                {t('deleteAccountPage.formSuccess')}
              </p>
            )}
            {status === 'err' && (
              <p className="form-status form-status--err" role="alert">
                {t('deleteAccountPage.formError', { privacyEmail: CONTACT.privacyEmail })}
              </p>
            )}
          </form>

          <p className="legal-paragraph delete-account-email-fallback">
            {t('deleteAccountPage.emailFallback')}{' '}
            <a
              href={`mailto:${CONTACT.privacyEmail}?subject=${encodeURIComponent(t('deleteAccountPage.emailSubject'))}`}
            >
              {CONTACT.privacyEmail}
            </a>
          </p>
        </article>
      </div>
    </>
  );
}
